import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export const WelcomeSplash = ({ onComplete }) => {
  const { splashColor, splashBadge, splashDisplayName } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Generate greeting message based on display name
  const greetingName = splashDisplayName || 'Coach';

  useEffect(() => {
    // Trigger the pop-in animation immediately
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    // Start exit animation after 2.5 seconds
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, 2500);

    // Complete and unmount after exit animation
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 3200);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center transition-opacity duration-700 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ backgroundColor: splashColor }}
      data-testid="welcome-splash"
    >
      {/* Club Badge */}
      <div 
        className={`transform transition-all duration-700 ease-out ${
          isVisible 
            ? 'scale-100 opacity-100 translate-y-0' 
            : 'scale-50 opacity-0 translate-y-8'
        }`}
      >
        {splashBadge ? (
          <img 
            src={splashBadge} 
            alt="Club Badge" 
            className="w-32 h-32 sm:w-40 sm:h-40 object-contain drop-shadow-2xl"
            data-testid="splash-club-badge"
          />
        ) : (
          // Default badge placeholder if no badge uploaded
          <div 
            className="w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-white/20 flex items-center justify-center"
            data-testid="splash-default-badge"
          >
            <svg 
              className="w-16 h-16 sm:w-20 sm:h-20 text-white/80" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
        )}
      </div>

      {/* Welcome Message */}
      <div 
        className={`mt-8 text-center transform transition-all duration-700 ease-out delay-200 ${
          isVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-4'
        }`}
      >
        <h1 
          className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg"
          style={{ fontFamily: "'Poppins', sans-serif" }}
          data-testid="splash-welcome-message"
        >
          Welcome Back {greetingName}!
        </h1>
      </div>

      {/* Subtle loading indicator */}
      <div 
        className={`mt-12 transition-all duration-500 delay-500 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="flex space-x-2">
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeSplash;
